/**
 * Automation: aka Autopilot works without any human intervention or supervision
 * AutomationJob = job container taht describes automation (trigger, cohorts and tasks)
 * AutomatTask = atomic unit of work that are context aware eg GenerateVoucher
 *
 * Automation Life Cycle
 * Job is "orchestrated" when triggered (schedule/ event/ user event)
 * which then "executes" described tasks
 * tasks are then "performed" in parallel
 */

import firebase from "firebase/compat/app";
import { TableMapType } from "./BigQuery/TableMaps";
import { Booking, BookingOrigin } from "./Booking";
// import { Customer } from "./customer";
import { hasRole, roles as roleBits } from "../roles";
import { CustomFilters, PurchaseScope } from "./CustomerFilter";
import { CustomerListFilter } from "./CustomerListFilter";
import { ReconcileOrigin } from "./MarketingMessage";
import { Membership } from "./Membership";
import { ContextInfoType, EmailEditorItem } from "./Newsletter";
import { Transaction } from "./Payments";

export const SYDNEY_TZ = "Australia/Sydney" as const;
export enum AutomationFrequency {
  OneOff = "OneOff",
  Reccuring = "Reccuring",
}

/**
 * Triggers
 * ------------------------------------------------------------------------------------------------------------
 *
 */
export enum AutomationTriggerType {
  Schedule = "Schedule",
  Event = "Event",
  UrlLink = "UrlLink",
}

// server events. eg when a new document is created in FireStore
export enum AllowedAutomationServerEvents {
  OnMembershipNew = "When a new Membership record is created",
  OnMembershipUpsert = "When a Membership record is updated",
  // OnCustomerNew = "When a new Customer record is created",
  // OnCustomerUpsert = "When a Customer record is updated",
  OnBookingUpsert = "When a Booking is updated",
  onBookingCreateNew = "When a new Booking is created",
  OnTransactionNew = "When a new Transaction is created",
}
// Declare the mapping once
export interface AutomationServerEventPayloadMap {
  // [AllowedAutomationServerEvents.OnCustomerNew]: Customer;
  // [AllowedAutomationServerEvents.OnCustomerUpsert]: Customer;
  [AllowedAutomationServerEvents.OnMembershipNew]: Membership;
  [AllowedAutomationServerEvents.OnMembershipUpsert]: Membership;
  [AllowedAutomationServerEvents.OnBookingUpsert]: Booking;
  [AllowedAutomationServerEvents.onBookingCreateNew]: Booking;
  [AllowedAutomationServerEvents.OnTransactionNew]: Transaction;
}

// user events. eg when user does something on client app. This is associates with UrlLink
export enum AllowedAutomationClientEvents {
  OnNewMembership = "When user joins membership program",
  OnMemberLogin = "When existing members logs into membership app",
}

// Trigger interfaces discriminated by type
export interface AutomationScheduleTrigger {
  type: AutomationTriggerType.Schedule;
  cron: string;
  nextRunAt?: firebase.firestore.Timestamp;
}

export interface AutomationEventTrigger {
  type: AutomationTriggerType.Event;
  eventName: AllowedAutomationServerEvents;
}
export interface AutomationUrlLinkTrigger {
  type: AutomationTriggerType.UrlLink;
  eventName: AllowedAutomationClientEvents;
}

/**
 * Automation Tasks
 * ------------------------------------------------------------------------------------------------------------
 * Note: Adding New Tasks:
 *  1. add new AllowedAutomationTaks member.
 *  2. create respective {MemberName}TaskParams interface
 *  3. update AutomationTaskParamsMap to include [AllowedAutomationTaks.{new Member}]: {MemberName}TaskParams
 *  4. in resbutler-data
      Add a case in the executeTask() switch
      Implement the run{TaskType}() function
 */
export enum AllowedAutomationTaks {
  Email = "Email",
  EmailVoucher = "EmailVoucher",
  GenerateVoucher = "GenerateVoucher",
}

// For email based on AutomationEmailTemplate
export interface EmailTaskParams {
  emailTemplateId: string;
  voucherSetupId: never;
  subject?: string;
}
// For email based on Voucher Email Branding Template
export interface EmailVoucherTaskParams {
  voucherSetupId: string;
  emailTemplateId: string;
  headerText: string;
  bodyText: string;
}

export interface GenerateVoucherTaskParams {
  voucherSetupId: string;
}

// Map of task types to their params interfaces
interface AutomationTaskParamsMap {
  [AllowedAutomationTaks.Email]: EmailTaskParams;
  [AllowedAutomationTaks.EmailVoucher]: EmailVoucherTaskParams;
  [AllowedAutomationTaks.GenerateVoucher]: GenerateVoucherTaskParams;
}

export type AutomationTask = {
  [K in AllowedAutomationTaks]: {
    type: K;
    params: AutomationTaskParamsMap[K];
  };
}[AllowedAutomationTaks];

/**
 * Job
 * ------------------------------------------------------------------------------------------------------------
 */
interface BaseAutomationJob {
  id: string; // Firestore doc ID
  title: string;
  /** Restaurant ID for the automation job */
  restaurantId?: string;
  tasks: AutomationTask[];
  /** to be used in conjunction with `isProvisioned`: Given that it's available, do I want to use it?
   * value will have no effect if isProvisioned is falsy */
  isEnabled: boolean;
  /** SystemAdmin only control. Indicates the document is provisioned (by System Admin or seed script) and therefore available or not for use*/
  isProvisioned: boolean;
  category: AutomationCategory;
  /** Editability/role model for this automation (Built in / Standard / Essential / Custom). */
  model?: AutomationModel;
  /** Stable key of the AvailableAutomation this instance was seeded/cloned from */
  sourceKey?: string;
  /** Exclude customers who were already matched/actioned in earlier runs of this job */
  uniqueChortsOnly?: boolean;
}

export interface ScheduledAutomationJob extends BaseAutomationJob {
  trigger: AutomationScheduleTrigger;
  /** Customer list filter configuration for scheduled jobs */
  customerFilters: CustomerListFilter; // turn this into object map with prop `order` that denotes the send order
  isSuspended: boolean; // indicates the job is supended. eg when one of the task fails or part of task returns error
  documentFitlers: never;
  documentTables: never;
  filters: never; // scheduled job is based on customerFilters not dynamnic filters
  selectedTables: never;
  purchaseMatchWithin?: never; // scope is read from each referenced CustomerSegment, not the job
  runInHours: never;
}

export interface EventAutomationJob extends BaseAutomationJob {
  trigger: AutomationEventTrigger;
  customerFilters: never;
  isSuspended: never;
  documentFitlers: CustomFilters; // filters applied to event document in context
  documentTables: TableMapType[];
  filters: CustomFilters; // filters applied to associated customer Id in event document in context
  selectedTables: TableMapType[];
  /** "Match within" scope for any purchase filter inside `filters`. defaults to Customer whcih is entire history */
  purchaseMatchWithin?: PurchaseScope;
  runInHours: number; // Job task run delay in hours, 0= immediate which is default
}

export interface UrlLinkAutomationJob extends BaseAutomationJob {
  trigger: AutomationUrlLinkTrigger;
  customerFilters: never;
  isSuspended: never;
  documentFitlers: never;
  documentTables: never;
  filters: never; // UrlLinkAutomation job is based on customerFilters not dynamnic filters
  selectedTables: never;
  purchaseMatchWithin?: never; // no dynamic filters -> no purchase scope
  runInHours: number; // Job task run delay in hours, 0= immediate which is default
}

// AutomationJob discriminated by trigger type
export type AutomationJob = ScheduledAutomationJob | EventAutomationJob | UrlLinkAutomationJob;

// Collection of jobs by id
export interface AutomationJobs {
  [id: string]: AutomationJob;
}

/**
 * Url Links
 */
export enum UrlLinkItemType {
  AutomationJob = "AutomationJob",
  Membership = "Membership",
}

export interface UrlLinkItem {
  id: string; // id of document type (below)
  type: UrlLinkItemType; // document type
  params?: any;
}

export interface UrlLinkItemContext<T> {
  type: UrlLinkItemType;
  clientEvent: AllowedAutomationClientEvents;
  restaurantId: string;
  membershipId?: string;
  payload?: T;
}

export interface AutomationEmailTemplate {
  id: string;
  createTime: number;
  description: string; // also used as email subject
  isEnabled: boolean; // also denotes ready for automation
  name: string;
  senderName: string;
  updatedAt: number;
  emailEditor: EmailEditorItem; // Unlayer Email editor props

  /**
   * System-reserved template seeded for a pre-built automation (SA-only).
   * Hidden from the operator-facing email-template list; edited only via automation Setup.
   */
  isReserved?: boolean;

  // Optionals
  _key?: string;
}

export interface AutomationEmailTemplates {
  [id: string]: AutomationEmailTemplate;
}

export enum CloudTaskType {
  AutoTag = "AutoTag",
  AutomationJob = "AutomationJob",
  MarketingSegment = "MarketingSegment",
  MarketingMessage = "MarketingMessage",
}

export type CloudTaskPhase = "ADD" | "REMOVE" | "DONE" | "CANCELLED" | "FAILED";
export type CloudTaskStatus = "SCHEDULED" | "RUNNING" | "DONE" | "FAILED" | "CANCELLED";

export interface CloudTaskItems {
  [id: string]: CloudTaskItem;
}
export interface CloudTaskItem {
  taskId: string;
  documentId: string;
  name: string;
  clientId: string;
  projectId: string;
  restaurantId: string;
  runPayload?: AutomationRunPayload;
  phase: CloudTaskPhase;
  results: {
    [key: string]: CloudTaskPhaseResult;
  };
  offset: number;
  processed: number;
  added: number;
  removed: number;
  startAt: number; // when should this task start
  completedAt: number; // every time phase is completed
  status: CloudTaskStatus;
  type: CloudTaskType;
  lastError?: string;
  activeTaskName?: string;
  totals: {
    desired: number;
    current: number;
    toAdd: number;
    toRemove: number;
  };
}

export type CreateCloudTaskOptions<TPayload> = {
  payload: TPayload;
  url: string;
  queue: string;
  clientId: string;
  projectId: string;
  headers?: Record<string, string>;
} & (
  | { delaySeconds: number; scheduleTimeSeconds?: never } //
  | { scheduleTimeSeconds: number; delaySeconds?: never }
);

export type CloudTaskPayload = {
  taskId: string;
  clientId: string;
  projectId: string;
};

export type FailedCreateCloudTask<TPayload> = Omit<CreateCloudTaskOptions<TPayload>, "delaySeconds" | "scheduleTimeSeconds"> & {
  delaySeconds?: number; // optional — only one will be present
  scheduleTimeSeconds?: number;
  failedAt: string;
  errorCode: number | null; // gRPC codes are numbers
  errorMessage: string | null;
};

export type CloudTaskStrategy<D, T, R> = {
  name: string;

  getDocument(task: CloudTaskItem, repositories: R): Promise<D>;

  getRestaurantId(doc: D): string | null;

  previouslyMatchedCustomerQuery(): string;
  buildRemoveSnapshotQuery(): string;

  queryParamName: string;

  addArrayField: keyof T;
  removeArrayField: keyof T;

  saveDone(task: CloudTaskItem, doc: D, addedCount: number, repositories: R): Promise<void>;
};

export interface CloudTaskPhaseResult {
  completedAt: number;
  /**
    how is Phase count value derived ??
    ADD:	updatedJob.added	Number of customers that should include this tag/ segment
    REMOVE:	updatedJob.removed	Number of customers that should not include this tag / segment
    DONE:	updatedJob.processed (or added + removed)	Total records processed
    FAILED:	updatedJob.processed	How far it got before failing
    CANCELLED:	updatedJob.processed	Partial progress before cancel
   */
  count: number;
}

// Union of all possible event-driven payloads
export type AutomationEventPayload = AutomationServerEventPayloadMap[keyof AutomationServerEventPayloadMap];

export interface AutomationRunPayload {
  restaurantId: string;
  customerId: string;
  membershipId?: string;
  origin?: ReconcileOrigin | ContextInfoType | BookingOrigin;
  originId?: string;
  pass?: 1 | 2;
}

export type CachedDocument<T = any> = {
  data: Record<string, T>; // jobId -> jobData
  expiresAt: number;
};

export type EnqueueTaskOption = {
  /** Id of parent FS document this task is based on */
  documentId: string;
  /** Name of parent FS doc this task is based on */
  documentName: string;
  /** AutomationJob | MarketingSegment ... */
  jobType: CloudTaskType;
  /** Callback url Clkoud task hits upon run */
  workerUrl: string;
  /** When this task should run */
  scheduleTime?: Date;
  /** Asscociated context payload for this task */
  runPayload?: AutomationRunPayload;
};

// Automation category
export enum AutomationCategory {
  Reservation = "booking_reservation",
  FunctionReservation = "function_reservation",
  Ordering = "ordering",
  Membership = "membership",
  Lifecycle = "lifecycle",
  BrandCrossSell = "brand_cross_sell",
}

export const AutomationCategoryLabel: Record<AutomationCategory, string> = {
  [AutomationCategory.Reservation]: "Booking related",
  [AutomationCategory.FunctionReservation]: "Function booking related", //	Works especially well for fine/formal dining",
  [AutomationCategory.Ordering]: "Dine in, takeaway and function ordering focused", //	Hard lapse — stronger offer needed",
  [AutomationCategory.Lifecycle]: "Customer milestones: date joined anniversary, birthdays",
  [AutomationCategory.BrandCrossSell]: "Cross promotion across other brands/ venues", //	High intent signal, time-sensitive",
  [AutomationCategory.Membership]: "Membership focused", //	Works across entry to premium membership tiers",
};

/** End U friendly Audience labels */
export const AutomationAppWiredAudience: Record<AutomationCategory, string> = {
  [AutomationCategory.Reservation]: "Booking Customer",
  [AutomationCategory.FunctionReservation]: "Booking Customer",
  [AutomationCategory.Ordering]: "Ordering Customer",
  [AutomationCategory.Membership]: "Member Customer",
  [AutomationCategory.Lifecycle]: "Associated Customers",
  [AutomationCategory.BrandCrossSell]: "Associated Customers",
};

export const AutomationCategoryOrder: AutomationCategory[] = [AutomationCategory.Reservation, AutomationCategory.FunctionReservation, AutomationCategory.Ordering, AutomationCategory.Membership, AutomationCategory.Lifecycle, AutomationCategory.BrandCrossSell];

/** Short badge label for lists */
export const AutomationCategoryShortLabel: Record<AutomationCategory, string> = {
  [AutomationCategory.Reservation]: "Bookings",
  [AutomationCategory.FunctionReservation]: "Functions",
  [AutomationCategory.Ordering]: "Ordering",
  [AutomationCategory.Membership]: "Membership",
  [AutomationCategory.Lifecycle]: "Lifecycle",
  [AutomationCategory.BrandCrossSell]: "Cross-sell",
};

/**
 * Automation Model
 * ------------------------------------------------------------------------------------------------------------
 * Determines the type, editability and role-based access of an automation.
 *
 *  A — BuiltIn  : deeply wired in the app workflow, ships as-is, cannot be disabled.
 *                 No FS doc in automation/jobs (defined only in the AvailableAutomations registry).
 *  B — Standard : on/off only. Authored by System admin (UI) or code.
 *  C — Essential: on/off + inline edit of one designated property directly from the list.
 *  D — Custom   : fully authored by both tenant user and System admin (the existing wizard flow).
 */
export enum AutomationModel {
  BuiltIn = "necessary", // A
  Optional = "optional", // B
  Flexible = "flexible", // C
  Custom = "custom", // D
}

/** Display order for grouping the automation list by model (A -> D). */
export const AutomationModelOrder: AutomationModel[] = [AutomationModel.BuiltIn, AutomationModel.Optional, AutomationModel.Flexible, AutomationModel.Custom];

export const AutomationModelLabel: Record<AutomationModel, string> = {
  [AutomationModel.BuiltIn]: "System",
  [AutomationModel.Optional]: "Optional",
  [AutomationModel.Flexible]: "Flexible",
  [AutomationModel.Custom]: "Custom",
};

export enum AutomationModelGroup {
  System = "system",
  Smart = "smart", //Optional + Flexible are shown as "Smart" im UI now
  Custom = "custom",
}

// Custom leads (mirrors the Marketing Segments list), then Smart, then System.
export const AutomationModelGroupOrder: AutomationModelGroup[] = [AutomationModelGroup.Custom, AutomationModelGroup.Smart, AutomationModelGroup.System];

export const AutomationModelGroupLabel: Record<AutomationModelGroup, string> = {
  [AutomationModelGroup.System]: "System",
  [AutomationModelGroup.Smart]: "Smart",
  [AutomationModelGroup.Custom]: "Custom",
};

export const AutomationModelGroupTagline: Record<AutomationModelGroup, string> = {
  [AutomationModelGroup.System]: "Core automations that simply run",
  [AutomationModelGroup.Smart]: "Set your preference. We do the rest.",
  [AutomationModelGroup.Custom]: "Your automation, your rules.",
};

export const getAutomationModelGroup = (model: AutomationModel): AutomationModelGroup => (model === AutomationModel.BuiltIn ? AutomationModelGroup.System : model === AutomationModel.Custom ? AutomationModelGroup.Custom : AutomationModelGroup.Smart);

/** Marketing one-liner shown under each model section heading. */
export const AutomationModelTagline: Record<AutomationModel, string> = {
  [AutomationModel.BuiltIn]: "Core automations that simply runs",
  [AutomationModel.Optional]: "Turn it on. Turn it off. We handle the rest.",
  [AutomationModel.Flexible]: "Set your preference. We do the rest.",
  [AutomationModel.Custom]: "Your automation, your rules.",
};

/** Read an automation's model, defaulting legacy/user-authored jobs to Custom. */
export const getAutomationModel = (job: Pick<BaseAutomationJob, "model">): AutomationModel => job.model ?? AutomationModel.Custom;

/**
 * Role-based access helpers
 * ------------------------------------------------------------------------------------------------------------ */

/** A simple on/off switch is shown in the list for Standard (B) and Essential (C) only. */
export const canToggleAutomationModel = (model: AutomationModel): boolean => model === AutomationModel.Optional || model === AutomationModel.Flexible;

/** Essential (C) exposes one inline-editable property directly from the list. */
export const canInlineEditAutomationModel = (model: AutomationModel): boolean => model === AutomationModel.Flexible;

/**
 * Who can open the full editor:
 *  - Built in (Necessary) (A): nobody (code-level only).
 *  - Custom (D): every tenant user (and SA).
 *  - Optional (B) / Flexible (C): System admin only.
 */
export const canEditAutomation = (model: AutomationModel, rolesBitmask: number): boolean => {
  if (model === AutomationModel.BuiltIn) return false;
  if (model === AutomationModel.Custom) return true;
  return !!hasRole(rolesBitmask, roleBits.SYSTEM);
};

/** Logs are visible for Standard/Essential/Custom to admins (SYSTEM | ADMIN). */
export const canViewAutomationLogs = (model: AutomationModel, rolesBitmask: number): boolean => {
  if (model === AutomationModel.BuiltIn) return false;
  return !!hasRole(rolesBitmask, roleBits.SYSTEM | roleBits.ADMIN);
};

export interface AutomationEmailTemplate {
  id: string;
  createTime: number;
  updatedAt: number;
  description: string;
  isEnabled: boolean;
  name: string;
  senderName: string;
  emailEditor: EmailEditorItem;
  _key?: string;
}
